<template>
  <div class="timeline-container border px-2 pb-1">
    <div
      class="d-flex justify-content-between align-items-center position-relative"
    >
      <span>
        <small class="me-1">{{
          dateHelper.getFormattedTime(currentTime)
        }}</small>
        <small
          v-if="isMarking && !isCycleEndExist && !videoSingleSequence"
          class="text-danger"
        >
          Start Selected, Adjust Slider and Press Space to mark video end
        </small>
      </span>

      <small
        class="position-absolute start-50 translate-middle-x"
        :style="{
          pointerEvents: isDisabled ? 'none' : 'auto',
        }"
      >
        <PauseOutlined
          v-if="isPlaying"
          class="fs-5"
          @click="toggleVideoHandler"
        />
        <CaretRightOutlined v-else class="fs-5" @click="toggleVideoHandler" />
      </small>

      <a-space class="mt-2">
        <small>Playback speed:</small>
        <a-select
          v-model:value="playbackRate"
          size="small"
          :options="playbackRatesOptions"
          :disabled="isDisabled"
          style="min-width: 60px"
          @change="handlePlaybackRate"
        />
        <small>{{ dateHelper.getFormattedTime(videoDuration) }}</small>
      </a-space>
    </div>

    <a-slider
      id="annotate-slider"
      class="mx-0 mt-1"
      :value="currentFrame"
      :min="0"
      :max="totalFrames"
      :disabled="isDisabled"
      @update:value="onSliderValueChange"
    />

    <div
      id="timeLine"
      :style="{ height: '20px' }"
      class="w-100 position-relative m-0 border d-flex"
    >
      <div class="hairline">
        <div class="hairline-top"></div>
      </div>

      <slider-painter
        :is-marking="isMarking"
        :context-menu-items="sliderVideoMenuItems"
        :interval="intervalPoints"
        :current-slider-percent="currentSliderPercentage"
        :single-sequence="videoSingleSequence"
        @removeInterval="removeVideo"
      />
    </div>
  </div>
</template>
<script>
import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons-vue';
import dateHelper from 'src/components/shared/Helpers/dateHelper';
import SliderPainter from './SliderPainter.vue';
import { frameType, sliderVideoMenuItems } from './config';
export default {
  components: {
    SliderPainter,
    PauseOutlined,
    CaretRightOutlined,
  },

  props: {
    isDisabled: { type: Boolean, default: false },
    isPlaying: { type: Boolean, default: false },
    isMarking: { type: Boolean, default: false },
    videoSingleSequence: { type: Boolean, default: false },
    totalFrames: { type: Number, default: 0 },
    currentFrame: { type: Number, default: 0 },
    videoDuration: { type: Number, default: 0 },
    currentTime: { type: Number, default: 0 },
    videoInfo: {
      type: Object,
      default: () => ({}),
    },
    videoPoints: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['toggleVideo', 'frameChange', 'setPlaybackRate', 'removeVideoCycles'],

  setup() {
    return {
      dateHelper,
      sliderVideoMenuItems,
    };
  },

  data() {
    return {
      currentSliderPercentage: 0,
      playbackRate: 1,
      isTrimming: false,
      intervalPoints: {
        start_frame: -1,
        end_frame: -1,
      },
    };
  },

  computed: {
    playbackRatesOptions() {
      return [1, 2, 4, 8, 16].map((rate) => {
        return { value: rate, label: `${rate}x` };
      });
    },

    isCycleEndExist() {
      return this.intervalPoints.end_frame !== -1;
    },
  },

  watch: {
    videoInfo() {
      this.intervalPoints.start_frame = -1;
      this.intervalPoints.end_frame = -1;
    },

    totalFrames(value) {
      if (!value) {
        this.intervalPoints.start_frame = -1;
        this.intervalPoints.end_frame = -1;
        return;
      }
      this.setVideoInterval();
    },

    currentFrame(frame) {
      if (!frame) {
        this.currentSliderPercentage = 0;
        return;
      }
      this.currentSliderPercentage = this.getSliderPercentage(frame);
    },

    videoPoints: {
      handler(object) {
        const points = object[this.videoInfo.fileName];
        this.setIntervalPoints(points);
      },
      deep: true,
    },
  },

  mounted() {
    this.setVideoInterval();
  },

  methods: {
    setVideoInterval() {
      if (
        !Object.prototype.hasOwnProperty.call(
          this.videoPoints,
          this.videoInfo.fileName
        )
      ) {
        this.intervalPoints.start_frame = -1;
        this.intervalPoints.end_frame = -1;
        return;
      }
      const points = this.videoPoints[this.videoInfo.fileName];
      this.setIntervalPoints(points);
    },

    setIntervalPoints(points) {
      if (!points) return;
      if (Object.prototype.hasOwnProperty.call(points, frameType.startFrame))
        this.intervalPoints.start_frame = this.getSliderPercentage(
          points[frameType.startFrame]
        );
      else this.intervalPoints.start_frame = -1;
      if (Object.prototype.hasOwnProperty.call(points, frameType.endFrame))
        this.intervalPoints.end_frame = this.getSliderPercentage(
          points[frameType.endFrame]
        );
      else this.intervalPoints.end_frame = -1;
    },

    toggleVideoHandler() {
      if (this.isDisabled) return;
      this.$emit('toggleVideo');
    },

    onSliderValueChange(frame) {
      this.$emit('frameChange', frame);
    },

    getSliderPercentage(frame) {
      return (frame / this.totalFrames) * 100;
    },

    handlePlaybackRate() {
      this.$emit('setPlaybackRate', this.playbackRate);
    },

    removeVideo() {
      this.$emit('removeVideoCycles');
    },
  },
};
</script>

<style scoped>
.hairline {
  height: 100%;
  width: 1px;
  background: black;
  position: relative;
  z-index: 1;
  left: calc(v-bind('currentSliderPercentage') * 1%);
}

.hairline-top {
  width: 10px;
  height: 10px;
  position: absolute;
  transform: translate(-45%, 0%);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid black;
}
</style>
